<template>
  <div class="persona-chart-viewport" :class="{ 'is-original-size': mode === 'original' }">
    <div v-show="hasChart" class="persona-chart-viewport-tools">
      <div role="group" aria-label="차트 표시 크기">
        <button type="button" :aria-pressed="mode === 'fit'" :disabled="!zoomSupported" @click="mode = 'fit'">폭에 맞춤</button>
        <button type="button" :aria-pressed="mode === 'original'" @click="mode = 'original'">원래 크기</button>
      </div>
      <span v-if="!zoomSupported">이 브라우저에서는 원래 크기로 표시합니다. 차트 안에서 스크롤해 주세요.</span>
      <span v-else-if="sizeKnown">{{ mode === 'fit' ? '전체 차트 ' + fitPercent + '%' : '원래 크기 · 가로로 스크롤할 수 있습니다' }}</span>
      <span v-else-if="sizeFallback">자동 맞춤을 사용할 수 없어 원래 크기로 표시합니다. 차트 안에서 스크롤해 주세요.</span>
      <span v-else>차트 크기를 확인하고 있습니다.</span>
    </div>
    <!-- Keep the slot and its iframe ancestors in place when the mode changes. -->
    <div ref="content" class="persona-chart-viewport-content"><slot /></div>
  </div>
</template>

<script>
const EVENTS = ['firstinteractive', 'firstvizsizeknown', 'tabswitched'];
const positive = value => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : 0;
const publishedSize = size => {
  if (!size) return null;
  const width = Math.max(positive(size.maxSize && size.maxSize.width), positive(size.minSize && size.minSize.width));
  const height = Math.max(positive(size.maxSize && size.maxSize.height), positive(size.minSize && size.minSize.height));
  return width && height ? { width, height } : null;
};

export default {
  name: 'PersonaChartViewport',
  props: { active: { type: Boolean, default: true } },
  data() {
    const zoomSupported = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1');
    return { mode: zoomSupported ? 'fit' : 'original', zoomSupported, hasChart: false, sizeKnown: false, sizeFallback: false, fitPercent: 100 };
  },
  watch: {
    active(value) { if (value) this.$nextTick(this.schedule); },
    mode() { this.schedule(); }
  },
  created() { this._charts = new Map(); this._frame = null; this._disposed = false; },
  mounted() {
    this.$el.addEventListener('expert-chart-load', this.onChartLoad);
    // Attribute changes include PersonaCharts' v-show, but never inspect the
    // cross-origin iframe or replace/reparent any Tableau DOM.
    this._mutations = new MutationObserver(this.schedule);
    this._mutations.observe(this.$refs.content, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    if (typeof ResizeObserver !== 'undefined') {
      this._resize = new ResizeObserver(this.schedule);
      this._resize.observe(this.$el);
    }
    window.addEventListener('resize', this.schedule);
    this.schedule();
  },
  activated() { this.schedule(); },
  beforeDestroy() {
    this._disposed = true;
    if (this._frame !== null) cancelAnimationFrame(this._frame);
    this.$el.removeEventListener('expert-chart-load', this.onChartLoad);
    window.removeEventListener('resize', this.schedule);
    if (this._mutations) this._mutations.disconnect();
    if (this._resize) this._resize.disconnect();
    this._charts.forEach((record, viz) => this.detach(viz, record));
    this._charts.clear();
  },
  methods: {
    onChartLoad(event) {
      if (event.detail && event.detail.state === 'ready') {
        const viz = event.target.querySelector('tableau-viz');
        if (viz) this.attach(viz).ready = true;
      }
      this.schedule();
    },
    schedule() {
      if (this._disposed || this._frame !== null) return;
      this._frame = requestAnimationFrame(() => { this._frame = null; this.measure(); });
    },
    attach(viz) {
      if (this._charts.has(viz)) return this._charts.get(viz);
      const record = { size: null, chromeHeight: 0, fallback: false, ready: false, dimensions: '', listener: null };
      record.listener = event => {
        if (event.type === 'firstinteractive' || event.type === 'tabswitched') record.ready = true;
        if (event.type === 'tabswitched') { record.size = null; record.fallback = false; }
        const known = event.detail && event.detail.vizSize;
        if (known) { record.size = publishedSize(known.sheetSize); record.chromeHeight = positive(known.chromeHeight); }
        this.schedule();
      };
      EVENTS.forEach(name => viz.addEventListener(name, record.listener));
      this._charts.set(viz, record);
      return record;
    },
    detach(viz, record) { EVENTS.forEach(name => viz.removeEventListener(name, record.listener)); },
    setStyle(element, property, value) {
      if (element.style.getPropertyValue(property) !== value) element.style.setProperty(property, value);
    },
    measure() {
      if (this._disposed || !this.$refs.content) return;
      const current = Array.from(this.$refs.content.querySelectorAll('tableau-viz'));
      this._charts.forEach((record, viz) => {
        if (!current.includes(viz)) { this.detach(viz, record); this._charts.delete(viz); }
      });
      current.forEach(viz => this.attach(viz));
      if (!this.active || !this.$el.clientWidth) return;
      const visible = current.filter(viz => viz.getClientRects().length && viz.closest('.tableau-embed'));
      this.hasChart = visible.length > 0;
      this.sizeKnown = false;
      this.sizeFallback = false;
      visible.forEach(viz => {
        const record = this._charts.get(viz), host = viz.closest('.tableau-embed');
        // The existing host is the viewport. Its width stays tied to the page,
        // rather than the unscaled fixed-width dashboard inside it.
        const available = host.clientWidth;
        if (!available) return;
        let sheet, size;
        try { sheet = viz.workbook && viz.workbook.activeSheet; size = publishedSize(sheet && sheet.size); } catch (_) { /* API not interactive yet. */ }
        size = size || record.size;
        if (!size) {
          host.removeAttribute('data-persona-sized');
          // Optional width/height default to the published view size in v3.
          // Do not constrain an unknown dashboard to the legacy 1000px height.
          if ((sheet || record.ready) && !record.fallback) {
            record.fallback = true;
            record.dimensions = '';
            viz.removeAttribute('width'); viz.removeAttribute('height');
            try { if (typeof viz.resize === 'function') viz.resize(); } catch (_) { /* Keep the published-size scroll fallback. */ }
          }
          this.sizeFallback = record.fallback;
          this.scrollAccess(host, true);
          return;
        }
        record.size = size;
        const width = Math.ceil(size.width), height = Math.ceil(size.height + record.chromeHeight);
        const displayMode = this.zoomSupported ? this.mode : 'original';
        const scale = displayMode === 'fit' ? Math.min(1, available / width) : 1;
        const dimensions = `${width}x${height}`;
        if (record.dimensions !== dimensions) {
          // Viz width/height are local embedding settings. We deliberately do
          // not call changeSizeAsync, any filter API, refresh, or auth methods.
          try {
            viz.width = String(width); viz.height = String(height);
            record.dimensions = dimensions;
            if (typeof viz.resize === 'function') viz.resize();
          } catch (_) { this.scrollAccess(host, true); return; }
        }
        if (host.getAttribute('data-persona-sized') !== displayMode) host.setAttribute('data-persona-sized', displayMode);
        this.setStyle(host, '--persona-viz-width', width + 'px');
        this.setStyle(host, '--persona-viz-height', height + 'px');
        this.setStyle(host, '--persona-viz-scale', String(scale));
        this.setStyle(host, '--persona-viz-display-height', Math.ceil(height * scale) + 'px');
        this.scrollAccess(host, displayMode === 'original');
        this.sizeKnown = true;
        this.fitPercent = Math.round(scale * 100);
      });
    },
    scrollAccess(host, scrollable) {
      if (scrollable) {
        host.setAttribute('tabindex', '0');
        host.setAttribute('role', 'region');
        host.setAttribute('aria-label', '분석 차트 · 스크롤하여 전체 보기');
      } else {
        host.removeAttribute('tabindex'); host.removeAttribute('role'); host.removeAttribute('aria-label');
      }
    }
  }
};
</script>

<style>
.persona-chart-viewport,.persona-chart-viewport-content { min-width: 0; max-width: 100%; width: 100%; }
.persona-chart-viewport-tools { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px 14px; margin: 0 0 12px; color: #526174; font-size: 13px; }
.persona-chart-viewport-tools > div { display: flex; gap: 2px; border: 1px solid #c6ced8; border-radius: 4px; padding: 2px; }
.persona-chart-viewport-tools button { padding: 6px 10px; min-height: 34px; color: #526174; font: inherit; font-size: 13px; border-radius: 2px; }
.persona-chart-viewport-tools button[aria-pressed=true] { background: #eaf0fc; color: #165bd8; font-weight: 650; }
.persona-chart-viewport-tools button:disabled { cursor: default; color: #526174; background: #f4f5f7; }
.persona-chart-viewport-tools button:focus-visible { outline: 2px solid #165bd8; outline-offset: 2px; }
.persona-chart-viewport .expert-chart-inline,.persona-chart-viewport .expert-chart-inline > .row { min-width: 0; max-width: 100%; width: 100%; }
.persona-chart-viewport .expert-chart-inline > .row { display: block; margin: 0; }
.persona-chart-viewport .expert-chart-inline .tableau-embed { position: relative; min-width: 0; max-width: 100%; width: 100%; overflow: auto; }
.persona-chart-viewport .expert-chart-inline .tableau-embed[data-persona-sized] { min-height: 0; height: var(--persona-viz-display-height); }
.persona-chart-viewport .tableau-embed[data-persona-sized=fit] { overflow: hidden; }
/* Native layout zoom keeps pointer hit testing aligned inside Tableau's iframe.
   Transform scaling rendered correctly but misrouted its dropdown clicks. */
.persona-chart-viewport .tableau-embed[data-persona-sized] > tableau-viz { display: block; width: var(--persona-viz-width); height: var(--persona-viz-height); zoom: var(--persona-viz-scale); }
</style>
